/**
 * Accordion Tabs — enhances [data-accordion-tabs] containers
 * by generating a tab bar from accordion-row summaries and
 * managing panel visibility via data-tab-active attributes.
 *
 * Works with the existing <accordion-custom> / <details> structure.
 */
class AccordionTabs {
  /** @type {HTMLElement} */
  #root;

  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {number} */
  #activeIndex = 0;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.#root = root;

    // Prevent double-init
    if (root.dataset.accordionTabsInit) return;
    root.dataset.accordionTabsInit = 'true';

    this.#panels = [...root.querySelectorAll(':scope > accordion-custom')];
    if (this.#panels.length === 0) return;

    const uid = root.id || Math.random().toString(36).slice(2);
    this.#buildTabBar(uid);
    this.#activateTab(this.#findDefaultIndex());
  }

  /**
   * Finds which tab should be active by default.
   * Prefers the first row with open-by-default, falls back to index 0.
   */
  #findDefaultIndex() {
    const idx = this.#panels.findIndex(
      (panel) =>
        panel.hasAttribute('open-by-default-on-desktop') || panel.hasAttribute('open-by-default-on-mobile')
    );
    return idx >= 0 ? idx : 0;
  }

  /** @param {string} uid */
  #buildTabBar(uid) {
    const bar = document.createElement('div');
    bar.classList.add('accordion__tab-bar');
    bar.setAttribute('role', 'tablist');

    this.#panels.forEach((panel, index) => {
      const summary = panel.querySelector('.details__header');
      const heading = summary ? summary.textContent.trim() : `Tab ${index + 1}`;

      const button = document.createElement('button');
      button.classList.add('accordion__tab-button');
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('tabindex', '-1');
      button.setAttribute('type', 'button');
      button.textContent = heading;

      button.addEventListener('click', () => this.#activateTab(index));
      button.addEventListener('keydown', (e) => this.#handleKeyDown(e, index));

      bar.appendChild(button);
      this.#tabs.push(button);

      // Set up panel ARIA
      const panelId = `tab-panel-${uid}-${index}`;
      const tabId = `tab-${uid}-${index}`;
      button.id = tabId;
      button.setAttribute('aria-controls', panelId);

      const detailsContent = panel.querySelector('.details-content');
      if (detailsContent) {
        detailsContent.id = panelId;
        detailsContent.setAttribute('role', 'tabpanel');
        detailsContent.setAttribute('aria-labelledby', tabId);
      }
    });

    this.#root.prepend(bar);
  }

  /** @param {number} index */
  #activateTab(index) {
    // Deactivate all
    this.#tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });
    this.#panels.forEach((panel) => {
      panel.removeAttribute('data-tab-active');
      const details = panel.querySelector('details');
      if (details) details.removeAttribute('open');
    });

    // Activate selected
    this.#tabs[index].setAttribute('aria-selected', 'true');
    this.#tabs[index].setAttribute('tabindex', '0');
    this.#panels[index].setAttribute('data-tab-active', '');

    const details = this.#panels[index].querySelector('details');
    if (details) details.setAttribute('open', '');

    this.#activeIndex = index;
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   */
  #handleKeyDown(event, currentIndex) {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(newIndex);
    this.#tabs[newIndex].focus();
  }
}

/** Initialize all accordion-tabs containers found in the DOM */
function initAccordionTabs(scope = document) {
  scope.querySelectorAll('[data-accordion-tabs]:not([data-accordion-tabs-init])').forEach(
    (el) => new AccordionTabs(el)
  );
}

// Run on initial load
initAccordionTabs();

// Re-run when Shopify theme editor re-renders sections
document.addEventListener('shopify:section:load', (e) => initAccordionTabs(e.target));
document.addEventListener('shopify:block:select', (e) => initAccordionTabs(e.target.closest('.shopify-section') || document));

// Observe DOM for dynamically added accordion-tabs containers
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.hasAttribute?.('data-accordion-tabs')) {
          new AccordionTabs(node);
        }
        node.querySelectorAll?.('[data-accordion-tabs]:not([data-accordion-tabs-init])').forEach(
          (el) => new AccordionTabs(el)
        );
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });
